import { CloseOutlined, DownloadOutlined, FilterAltOutlined, Search, StarBorder, ViewWeekOutlined } from '@mui/icons-material'
import { Box, Button, IconButton, InputAdornment, Stack, Theme, Tooltip, Typography } from '@mui/material'
import {
  GridColDef,
  GridFilterModel,
  gridFilterModelSelector,
  GridSlotsComponentsProps,
  GridToolbarColumnsButton,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium'
import uniq from 'lodash/uniq'
import { PropsWithChildren, ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { requestsTableName } from '@omnea-app/fe-requests/requestsTableName'
import { filterItemIsValid } from '@omnea-app/legacy/utils/savedFilters/validateFilterModel'
import { isDefined } from '@omnea-app/legacy/utils/type-guard/isDefined'
import { AiFilterCreator } from '../../ai-filter-creator/AiFilterCreator'
import { RowGrouping } from '../../row-grouping/RowGrouping'
import { useSavedFilterContext } from './SavedFilterContext'

export type ExportOptions = {
  fileName: string
  fields?: string[]
}

declare module '@mui/x-data-grid-premium' {
  interface ToolbarPropsOverrides {
    tableName: string
    exportOptions: ExportOptions
    displayRowGroupingButton?: boolean
    hideSearchBar?: boolean
    disableExportInToolbar?: boolean
    exportLoading?: boolean
    isToolbarEmpty?: boolean
    header?: ReactNode
    onExportClick?: () => void
  }
}

export const ControlledTableToolbar = ({
  tableName,
  exportOptions,
  displayRowGroupingButton,
  hideSearchBar,
  disableExportInToolbar,
  exportLoading,
  header,
  isToolbarEmpty,
  onExportClick,
}: NonNullable<GridSlotsComponentsProps['toolbar']>) => {
  const { t } = useTranslation('common')
  const { savedFilter, onUnselectSavedFilter } = useSavedFilterContext()
  const apiRef = useGridApiContext()
  const { GEN_AI } = useFeatureFlags()

  const areColumnsGroupable = useMemo(() => {
    const columns = apiRef.current?.getAllColumns()
    return columns?.some((column) => column.groupable)
  }, [apiRef])

  const filterModel = useGridSelector(apiRef, gridFilterModelSelector)

  const filteredColumnsNames = getFilteredColumnNames(filterModel, apiRef.current?.getColumn)

  if (isToolbarEmpty) return null

  return (
    <Stack
      sx={{
        bgcolor: 'neutralLight.0',
        minWidth: '100%',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        border: '1px solid #E7E7ED',
        borderBottom: 'none',
      }}
    >
      {header && header}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 2, px: 4 }}>
        <Stack
          sx={(theme: Theme) => ({
            flexDirection: 'row',
            gap: 3,
            '& .MuiButton-startIcon': {
              display: 'flex',
              justifyContent: 'center',
              borderRadius: '4px',

              margin: 0,
              padding: 0,

              textTransform: 'none',
            },
            '& > *': {
              height: 24,
              borderRadius: '8px',

              margin: 0,
            },
            '& .MuiButtonBase-root': {
              padding: '3px 6px',
              background: 'none',
              '&:hover': {
                backgroundColor: theme.palette.neutralLight[50],
              },
              '&.active': {
                border: `1px solid ${theme.palette.neutralLight[300]}`,
                backgroundColor: theme.palette.neutralLight[50],
                '&:hover': {
                  backgroundColor: theme.palette.neutralLight[100],
                },
              },
            },
          })}
        >
          {savedFilter && <SavedFilterBadge>{savedFilter.name}</SavedFilterBadge>}

          <GridToolbarFilterButton
            slotProps={{
              button: {
                className: filteredColumnsNames ? 'active' : undefined,
                color: 'neutralLight',
                startIcon: (
                  <Stack
                    direction="row"
                    gap="2px"
                    sx={{
                      color: '#6B6C7E',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <FilterAltOutlined sx={{ width: 16, m: 0, p: 0 }} />
                    <Typography sx={{ m: 0, p: 0, ml: '1px' }} variant="Body14pxMedium">
                      {filteredColumnsNames
                        ? t('table.table-toolbar.active-filter-button-label', { columnList: filteredColumnsNames.join(', ') })
                        : t('table.table-toolbar.empty-filter-button-label')}
                    </Typography>
                    {filteredColumnsNames && filteredColumnsNames.length > 0 && (
                      <IconButton
                        sx={{ padding: '0px !important', margin: '1px -2px 0px 2px' }}
                        onClick={(e) => {
                          e.stopPropagation()
                          apiRef.current?.setFilterModel({ items: [] })
                          if (savedFilter) {
                            onUnselectSavedFilter()
                          }
                        }}
                      >
                        <CloseOutlined sx={{ fontSize: '16px' }} />
                      </IconButton>
                    )}
                  </Stack>
                ),
                variant: 'contained',
              },
            }}
          />
          {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: bulk migration to add error  */}
          {GEN_AI && tableName === requestsTableName && <AiFilterCreator tableName={tableName!} apiRef={apiRef} />}
          {displayRowGroupingButton && areColumnsGroupable && <RowGrouping />}
          <Tooltip title={t('table.table-toolbar.tooltip-columns')}>
            <GridToolbarColumnsButton
              slotProps={{
                button: {
                  color: 'neutralLight',
                  startIcon: (
                    <Stack
                      direction="row"
                      gap="2px"
                      sx={{ color: '#6B6C7E', justifyContent: 'center', alignItems: 'center', textTransform: 'none' }}
                    >
                      <ViewWeekOutlined sx={{ width: 16 }} />
                      <Typography sx={{ m: 0, p: 0, ml: '1px' }} variant="Body14pxMedium">
                        {t('table.table-toolbar.columns-button-label')}
                      </Typography>
                    </Stack>
                  ),
                },
              }}
            />
          </Tooltip>
          {!disableExportInToolbar && (
            <Tooltip title={exportLoading ? t('table.table-toolbar.tooltip-export-loading') : ''}>
              <span style={{ display: 'flex', alignItems: 'center' }}>
                {onExportClick != null ? (
                  <Button
                    color="neutralLight"
                    variant="contained"
                    disabled={exportLoading}
                    onClick={onExportClick}
                    sx={exportLoading ? { '&.Mui-disabled': { backgroundColor: 'transparent' } } : {}}
                    startIcon={
                      <Stack
                        direction="row"
                        gap="2px"
                        sx={(theme: Theme) => ({
                          color: exportLoading ? theme.palette.neutralLight[300] : 'inherit',
                          justifyContent: 'center',
                          alignItems: 'center',
                        })}
                      >
                        <DownloadOutlined sx={{ width: 16 }} />
                        <Typography sx={{ m: 0, p: 0, ml: '1px' }} variant="Body14pxMedium">
                          {t('table.table-toolbar.export-button-label')}
                        </Typography>
                      </Stack>
                    }
                  />
                ) : (
                  <GridToolbarExport
                    csvOptions={exportOptions}
                    excelOptions={exportOptions}
                    slotProps={{
                      button: {
                        color: 'neutralLight',
                        variant: 'contained',
                        disabled: exportLoading,
                        sx: exportLoading ? { '&.Mui-disabled': { backgroundColor: 'transparent' } } : {},
                        startIcon: (
                          <Stack
                            direction="row"
                            gap="2px"
                            sx={(theme: Theme) => ({
                              color: exportLoading ? theme.palette.neutralLight[300] : 'inherit',
                              justifyContent: 'center',
                              alignItems: 'center',
                            })}
                          >
                            <DownloadOutlined sx={{ width: 16 }} />
                            <Typography sx={{ m: 0, p: 0, ml: '1px' }} variant="Body14pxMedium">
                              {t('table.table-toolbar.export-button-label')}
                            </Typography>
                          </Stack>
                        ),
                      },
                    }}
                  />
                )}
              </span>
            </Tooltip>
          )}
        </Stack>
        {!hideSearchBar && (
          <GridToolbarQuickFilter
            variant="outlined"
            placeholder={t('search')}
            debounceMs={300}
            InputProps={{
              startAdornment: null,
              endAdornment: SearchEndAdornment,
              sx: {
                borderRadius: 8,
                height: '28px',
                width: 250,
                mt: '3px',
                bgcolor: 'neutralLight.0',
              },
            }}
          />
        )}
      </Stack>
    </Stack>
  )
}

const SearchEndAdornment = (
  <InputAdornment position="end">
    <IconButton edge="end" sx={{ position: 'absolute', right: 10 }}>
      <Search sx={{ width: 18, transform: 'scaleX(-1)' }} />
    </IconButton>
  </InputAdornment>
)

const getFilteredColumnNames = (
  filterModel: GridFilterModel | undefined,
  getColumn: undefined | ((field: string) => GridColDef)
): string[] | null => {
  if (!filterModel || !getColumn) return null
  const filterItems = filterModel?.items

  if (!filterItems) return null

  const allFilteredColumnNames = filterItems.filter(filterItemIsValid).map(({ field }) => field)

  if (allFilteredColumnNames.length < 1) return null

  const uniqueNames = uniq(allFilteredColumnNames)
    .map(getColumn)
    .map((columnDef) => columnDef?.headerName)
    .filter(isDefined)

  if (uniqueNames.length < 1) return null

  return uniqueNames
}

const SavedFilterBadge: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <Box
      sx={(theme: Theme) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2px',

        padding: '3px 6px',
        color: theme.palette.neutralLight[50],
        backgroundColor: theme.palette.neutralDark[700],
      })}
    >
      <StarBorder sx={{ width: 16, height: 16 }} />
      <Typography variant="Body12pxMedium">{children}</Typography>
    </Box>
  )
}
